/**
 * AgentDrawer - Main drawer component for agent details
 *
 * Phase 3 implementation with:
 * - URL state integration via global state
 * - Tab navigation with Health + Praetorian-only tabs (Tasks, Tunnel, Users, Jobs)
 * - Proper loading/error states
 * - ESC key and close button handling (via Drawer component)
 * - Task monitoring with adaptive polling for Tasks tab
 */

import { ClockIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Loader } from '@/components/Loader';
import { AgentActions } from '@/sections/agents/components/actions';
import { AddCredentialModal } from '@/sections/agents/components/aegis-management/credentials/AddCredentialModal';
import { useTaskMonitoring } from '@/sections/agents/components/aegis-management/task/useTaskMonitoring';
import { ProvisionTunnelModal } from '@/sections/agents/components/aegis-management/tunnel/ProvisionTunnelModal';
import { AddUserModal } from '@/sections/agents/components/aegis-management/user/AddUserModal';
import { getAgentHealthStatus } from '@/sections/agents/components/health/agentHealthUtils';
import { AGENT_DRAWER_DEFAULT_TAB } from '@/sections/agents/constants';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';

import { AgentDrawerTabs } from './AgentDrawerTabs';
import { useAgentTabs } from './hooks';
import { useAgentDrawerData } from './hooks/useAgentDrawerData';
import { useAgentDrawerTab } from './hooks/useAgentDrawerTab';
import { CredentialsTab } from './tabs/CredentialsTab';
import { HealthTab } from './tabs/HealthTab';
import { JobsTab } from './tabs/JobsTab';
import { OverviewTab } from './tabs/OverviewTab';
import { TasksTab } from './tabs/TasksTab';
import { TunnelTab } from './tabs/TunnelTab';
import { UsersTab } from './tabs/UsersTab';
import type { AgentDrawerTabId } from './types';

export function AgentDrawer() {
  const { drawerState } = useGlobalState();
  const { isPraetorianUser } = useAuth();
  const agentId = drawerState.agentDrawerKey || null;

  // Track expanded tasks state (local to drawer)
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const hasInitializedExpansion = useRef(false);

  // Modal state for Praetorian-only actions
  const [showAddCredentialModal, setShowAddCredentialModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showProvisionTunnelModal, setShowProvisionTunnelModal] =
    useState(false);

  // Fetch agent data - uses initialData from global state if available
  const {
    data: agent,
    isPending,
    error,
    refetch,
  } = useAgentDrawerData(agentId);

  // Use agent from global state as fallback for immediate display
  const displayAgent = agent || drawerState.agent;

  // Get current tab from local state hook (prevents race conditions with arrow keys)
  const [tabState, setCurrentTab] = useAgentDrawerTab();
  const currentTab = (tabState as AgentDrawerTabId) || AGENT_DRAWER_DEFAULT_TAB;

  // Task monitoring - only active for Praetorian users when drawer is open
  const isDrawerOpen = !!agentId;
  const taskMonitoring = useTaskMonitoring(
    displayAgent?.client_id,
    isDrawerOpen && isPraetorianUser
  );

  // Check if there are active tasks for badge
  const hasActiveTasks = useMemo(
    () => taskMonitoring.hasActiveTasks(),
    [taskMonitoring]
  );

  // Initialize expanded state for most recent task (only once when tasks first load)
  useEffect(() => {
    const mostRecentTask = taskMonitoring.getMostRecentTask();
    // Only initialize if we haven't done so before and we have tasks
    if (mostRecentTask && !hasInitializedExpansion.current) {
      setExpandedTasks(new Set([mostRecentTask.key]));
      hasInitializedExpansion.current = true;
    }
  }, [taskMonitoring]);

  // Reset expanded state when agent changes
  useEffect(() => {
    hasInitializedExpansion.current = false;
    setExpandedTasks(new Set());
  }, [agentId]);

  // Get filtered tabs based on user role and task state
  const tabs = useAgentTabs(isPraetorianUser, hasActiveTasks);

  // Normalize currentTab against available tabs to prevent empty content
  // when customer user opens URL with Praetorian-only tab (e.g., ?agentDrawerTab=tasks)
  // React Compiler handles memoization automatically - no useMemo needed
  const availableTabValues = tabs.map(tab => tab.value);
  const normalizedTab = availableTabValues.includes(currentTab)
    ? currentTab
    : (tabs[0]?.value ?? AGENT_DRAWER_DEFAULT_TAB);

  // Sync normalized tab to URL when different (external system sync)
  useEffect(() => {
    if (tabs.length > 0 && normalizedTab !== currentTab) {
      setCurrentTab(normalizedTab);
    }
  }, [normalizedTab, currentTab, setCurrentTab, tabs.length]);

  // Use normalizedTab for all content rendering (not currentTab)
  const activeTab = normalizedTab as AgentDrawerTabId;

  // Loading state
  if (isPending && !displayAgent) {
    return (
      <div
        className="flex size-full min-w-[400px] items-center justify-center"
        data-testid="agent-drawer-loading"
      >
        <Loader isLoading type="spinner" />
      </div>
    );
  }

  // Error state
  if (error && !displayAgent) {
    return (
      <div
        className="flex size-full min-w-[400px] flex-col items-center justify-center gap-4 p-6"
        data-testid="agent-drawer-error"
      >
        <ClockIcon className="text-chariot-stroke size-6" />
        <span className="text-chariot-text-secondary text-center">
          {error instanceof Error
            ? error.message
            : 'Unable to load agent details'}
        </span>
        <button
          onClick={() => refetch()}
          className="text-chariot-brand-primary hover:text-chariot-brand-primary/80 text-sm underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // No agent found (deleted or invalid ID)
  if (!displayAgent) {
    return (
      <div
        className="flex size-full min-w-[400px] items-center justify-center gap-4 p-6"
        data-testid="agent-drawer-not-found"
      >
        <ClockIcon className="text-chariot-stroke size-6" />
        <span className="text-chariot-text-secondary">
          Agent is no longer available
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex size-full flex-col gap-6 p-6"
      data-testid="agent-drawer"
      role="dialog"
      aria-labelledby="agent-drawer-title"
      aria-describedby="agent-drawer-description"
    >
      {/* Header */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2
            id="agent-drawer-title"
            className="text-chariot-text-primary text-xl font-semibold"
          >
            {displayAgent.fqdn || displayAgent.hostname || 'Agent Details'}
          </h2>
          {getAgentHealthStatus(displayAgent)}
        </div>
        {displayAgent.client_id && (
          <p
            id="agent-drawer-description"
            className="text-chariot-text-secondary text-sm"
          >
            Agent ID: {displayAgent.client_id}
          </p>
        )}
      </div>

      {/* Actions */}
      <AgentActions
        agent={displayAgent}
        isPraetorianUser={isPraetorianUser}
        onAddCredentialClick={() => setShowAddCredentialModal(true)}
        onAddUserClick={() => setShowAddUserModal(true)}
        onProvisionTunnelClick={() => setShowProvisionTunnelModal(true)}
        hideLabel
      />

      {/* Divider */}
      <div className="bg-chariot-stroke h-px w-full shrink-0" />

      {/* Tab Navigation - only show if user has access to multiple tabs */}
      {tabs.length > 1 && (
        <AgentDrawerTabs
          tabs={tabs}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
        />
      )}

      {/* Tab Content */}
      <div
        className="border-chariot-stroke flex-1 overflow-y-auto rounded-md border"
        data-testid="agent-drawer-content"
        role="tabpanel"
        aria-labelledby={`${activeTab}-tab`}
      >
        {activeTab === 'overview' && <OverviewTab agent={displayAgent} />}
        {activeTab === 'health' && (
          <HealthTab agent={displayAgent} isLoading={isPending && !!agent} />
        )}
        {activeTab === 'credentials' && <CredentialsTab agent={displayAgent} />}
        {activeTab === 'tasks' && isPraetorianUser && (
          <TasksTab
            taskMonitoring={taskMonitoring}
            expandedTasks={expandedTasks}
            setExpandedTasks={setExpandedTasks}
          />
        )}
        {activeTab === 'tunnel' && isPraetorianUser && (
          <TunnelTab
            agent={displayAgent}
            taskMonitoring={taskMonitoring}
            setSelectedTabValue={setCurrentTab}
            isDrawerOpen={isDrawerOpen}
          />
        )}
        {activeTab === 'users' && isPraetorianUser && (
          <UsersTab
            agent={displayAgent}
            taskMonitoring={taskMonitoring}
            setSelectedTabValue={setCurrentTab}
            isDrawerOpen={isDrawerOpen}
          />
        )}
        {activeTab === 'jobs' && isPraetorianUser && (
          <JobsTab agent={displayAgent} isActiveTab={activeTab === 'jobs'} />
        )}
      </div>

      {/* Praetorian-only action modals */}
      {isPraetorianUser && displayAgent && (
        <>
          <AddCredentialModal
            isOpen={showAddCredentialModal}
            onClose={() => setShowAddCredentialModal(false)}
            agent={displayAgent}
          />
          <AddUserModal
            isOpen={showAddUserModal}
            onClose={() => setShowAddUserModal(false)}
            agent={displayAgent}
          />
          <ProvisionTunnelModal
            isOpen={showProvisionTunnelModal}
            onClose={() => setShowProvisionTunnelModal(false)}
            agent={displayAgent}
            onSuccess={() => setShowProvisionTunnelModal(false)}
          />
        </>
      )}
    </div>
  );
}
